import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import AuthService from '../../services/authService';
import { apiErrToMessage } from '../../utils/apiError';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './AutoComplete.css';
import {
    Button,
    Input,
    Table,
    message,
    Modal,
    Space,
    Typography,
    List,
    Empty,
    Spin,
    Divider,
    Dropdown,
    Tooltip,
    Tag,
} from 'antd';
import {
    PlusOutlined,
    ReloadOutlined,
    DeleteOutlined,
    HistoryOutlined,
    LoadingOutlined,
    ClockCircleOutlined,
    UpOutlined,
    DownOutlined,
    VerticalAlignTopOutlined,
    VerticalAlignBottomOutlined,
    HolderOutlined,
    SearchOutlined,
    DownloadOutlined,
    UnorderedListOutlined,
    MenuOutlined,
    EditOutlined,
    FileAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;
const PAGE_SIZE = 10;

const VolumeTag = ({ vol, showDetail = false }) => {
    if (vol == null) return null;
    const total = typeof vol === 'object' ? vol.total : vol;
    if (!showDetail || typeof vol !== 'object') {
        return (
            <span style={{ fontSize: 11, marginLeft: 3, color: '#4096ff', fontWeight: 500 }}>
                [{total}]
            </span>
        );
    }
    return (
        <span style={{ fontSize: 11, marginLeft: 3 }}>
            <span style={{ color: '#4096ff', fontWeight: 500 }}>[{vol.total}</span>
            <span style={{ color: '#888', margin: '0 2px' }}>·</span>
            <span style={{ color: '#52c41a' }}>PC {vol.pc}</span>
            <span style={{ color: '#888', margin: '0 2px' }}>·</span>
            <span style={{ color: '#fa8c16' }}>M {vol.mobile}</span>
            <span style={{ color: '#4096ff', fontWeight: 500 }}>]</span>
        </span>
    );
};
const RESEARCH_COOLDOWN_MS = 10 * 60 * 1000;

const getResearchCooldownState = record => {
    if (!record.last_searched_at) return { disabled: false, remaining: 0 };
    const elapsed = Date.now() - new Date(record.last_searched_at).getTime();
    if (elapsed >= RESEARCH_COOLDOWN_MS) return { disabled: false, remaining: 0 };
    return { disabled: true, remaining: Math.ceil((RESEARCH_COOLDOWN_MS - elapsed) / 60000) };
};

const MemoCell = ({ text }) => {
    const [expanded, setExpanded] = React.useState(false);
    const ref = React.useRef(null);
    const [clamped, setClamped] = React.useState(false);

    React.useEffect(() => {
        if (ref.current) {
            setClamped(ref.current.scrollHeight > ref.current.clientHeight + 2);
        }
    }, [text]);

    return (
        <div>
            <div
                ref={ref}
                className="ac-memo-cell"
                style={
                    expanded
                        ? {}
                        : {
                              WebkitLineClamp: 1,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                          }
                }
            >
                {text}
            </div>
            {(clamped || expanded) && (
                <Button
                    type="link"
                    size="small"
                    style={{ padding: '0', height: 'auto', fontSize: '12px' }}
                    onClick={() => setExpanded(v => !v)}
                >
                    {expanded ? '접기' : '펼치기'}
                </Button>
            )}
        </div>
    );
};

const AutoComplete = () => {
    const [keyword, setKeyword] = useState('');
    const [rows, setRows] = useState([]);
    const [maxKeywords, setMaxKeywords] = useState(20);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        setIsMobile(window.innerWidth < 768);
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [reSearchingIds, setReSearchingIds] = useState(new Set());
    const [volumeUpdatingIds, setVolumeUpdatingIds] = useState(new Set());
    const [bulkModal, setBulkModal] = useState({ open: false, text: '', loading: false });
    const [memoModal, setMemoModal] = useState({ open: false, id: null, value: '' });
    const [memoSaving, setMemoSaving] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [expandedResultIds, setExpandedResultIds] = useState(new Set());

    // 결과 이력 모달 (행별)
    const [resultHistory, setResultHistory] = useState({
        open: false,
        id: null,
        keyword: '',
        rows: [],
        loading: false,
        loadingMore: false,
        hasMore: false,
        offset: 0,
    });
    const _resultHistoryBodyRef = React.useRef(null);

    // 조회 기록 모달
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);

    const fetchList = useCallback(async (search = '') => {
        setLoading(true);
        try {
            const res = await AuthService.authAxios.get('/autocomplete/list', {
                params: search.trim() ? { search: search.trim() } : {},
            });
            setRows(res.data.rows || []);
            if (res.data.maxKeywords != null) setMaxKeywords(res.data.maxKeywords);
        } catch (err) {
            message.error(apiErrToMessage(err.response?.data, err.response?.status));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleSearch = () => {
        fetchList(searchText);
    };

    const handleReset = () => {
        setSearchText('');
        fetchList('');
    };

    const handleExcelDownload = () => {
        if (rows.length === 0) {
            message.warning('다운로드할 데이터가 없습니다.');
            return;
        }
        const data = rows.map(r => {
            let volumes = {};
            try {
                volumes = r.search_volumes ? JSON.parse(r.search_volumes) : {};
            } catch (_e) {
                /* invalid JSON */
            }
            const hasVolumes = Object.keys(volumes).length > 0;
            const resultsText = (r.autocomplete_results || '')
                .split(',')
                .map(k => {
                    const kw = k.trim();
                    if (!hasVolumes || volumes[kw] == null) return kw;
                    const vol = volumes[kw];
                    return typeof vol === 'object'
                        ? `${kw} [총 ${vol.total} / PC ${vol.pc} / M ${vol.mobile}]`
                        : `${kw} [${vol}]`;
                })
                .join('\n');
            return {
                검색어: r.keyword,
                추출검색어: resultsText,
                메모: r.memo || '',
                등록일: dayjs(r.created_at).format('YYYY-MM-DD HH:mm'),
                최종조회일: dayjs(r.last_searched_at).format('YYYY-MM-DD HH:mm'),
            };
        });

        const ws = XLSX.utils.json_to_sheet(data);

        // 열 너비
        ws['!cols'] = [
            { wch: 20 }, // 검색어
            { wch: 50 }, // 추출검색어
            { wch: 40 }, // 메모
            { wch: 18 }, // 등록일
            { wch: 18 }, // 최종조회일
        ];

        // 헤더 색상 + 전체 셀 줄바꿈/상단정렬
        const range = XLSX.utils.decode_range(ws['!ref']);
        const headerStyle = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '4472C4' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        };
        const bodyStyle = { alignment: { vertical: 'top', wrapText: true } };

        for (let c = range.s.c; c <= range.e.c; c++) {
            const hAddr = XLSX.utils.encode_cell({ r: 0, c });
            if (ws[hAddr]) ws[hAddr].s = headerStyle;
        }
        for (let r = range.s.r + 1; r <= range.e.r; r++) {
            for (let c = range.s.c; c <= range.e.c; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                if (ws[addr]) ws[addr].s = bodyStyle;
            }
        }

        // 행 높이 설정 (메모 줄바꿈 반영)
        const MEMO_COL_CHARS = 40;
        const LINE_PT = 15;
        const rowHeights = [{ hpt: LINE_PT }]; // 헤더
        data.forEach(row => {
            const memo = row['메모'] || '';
            const lines = memo.split('\n');
            const totalLines = lines.reduce(
                (acc, line) => acc + Math.max(1, Math.ceil((line.length || 1) / MEMO_COL_CHARS)),
                0
            );
            rowHeights.push({ hpt: Math.max(LINE_PT, totalLines * LINE_PT) });
        });
        ws['!rows'] = rowHeights;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '자동완성조회');
        XLSX.writeFile(wb, `자동완성조회_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`, {
            cellStyles: true,
        });
    };

    const handleRegister = async () => {
        if (!keyword.trim()) {
            message.warning('키워드를 입력해주세요.');
            return;
        }
        const trimmed = keyword.trim().toLowerCase();
        if (rows.some(r => r.keyword.trim().toLowerCase() === trimmed)) {
            message.warning('이미 등록된 검색어입니다. 재검색을 이용해주세요.');
            return;
        }
        setRegisterLoading(true);
        try {
            const res = await AuthService.authAxios.post('/autocomplete/register', {
                keyword: keyword.trim(),
            });
            const { row } = res.data;
            setRows(prev => [row, ...prev]);
            message.success('키워드가 등록되었습니다.');
            window.dispatchEvent(new Event('autoCompleteQuotaUpdated'));
            setKeyword('');
        } catch (err) {
            message.error(apiErrToMessage(err.response?.data, err.response?.status));
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleReSearch = async id => {
        setReSearchingIds(prev => new Set(prev).add(id));
        try {
            const [res] = await Promise.all([
                AuthService.authAxios.post(`/autocomplete/re-search/${id}`),
                new Promise(r => setTimeout(r, 1500)),
            ]);
            const { row } = res.data;
            setRows(prev => prev.map(r => (r.id === id ? row : r)));
            window.dispatchEvent(new Event('autoCompleteQuotaUpdated'));
            message.success('재검색 완료');
        } catch (err) {
            if (err.response?.data?.overLimit) {
                Modal.warning({ title: '키워드 한도 초과', content: err.response.data.error });
            } else {
                message.error(apiErrToMessage(err.response?.data, err.response?.status));
            }
        } finally {
            setReSearchingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    // 대량 등록 미리보기 계산
    const bulkPreview = React.useMemo(() => {
        const existingSet = new Set(rows.map(r => r.keyword.trim().toLowerCase()));
        const availableSlots = Math.max(0, maxKeywords - rows.length);
        const lines = bulkModal.text
            .split(/[\n,]+/)
            .map(k => k.trim())
            .filter(Boolean);
        const seen = new Set();
        const toAdd = [];
        const duplicates = [];
        for (const kw of lines) {
            const kwLower = kw.toLowerCase();
            if (seen.has(kwLower)) continue;
            seen.add(kwLower);
            if (existingSet.has(kwLower)) {
                duplicates.push(kw);
            } else {
                toAdd.push(kw);
            }
        }
        const limited = toAdd.slice(0, availableSlots);
        return { toAdd: limited, duplicates, totalInput: seen.size, availableSlots };
    }, [bulkModal.text, rows, maxKeywords]);

    const handleBulkRegister = async () => {
        if (bulkPreview.toAdd.length === 0) {
            message.warning('추가할 키워드가 없습니다.');
            return;
        }
        setBulkModal(prev => ({ ...prev, loading: true }));
        try {
            const res = await AuthService.authAxios.post('/autocomplete/bulk-register', {
                keywords: bulkPreview.toAdd,
            });
            const { added } = res.data;
            if (added.length > 0) {
                setRows(prev => [...added, ...prev]);
                window.dispatchEvent(new Event('autoCompleteQuotaUpdated'));
                message.success(`${added.length}개 등록 완료. 검색량을 순서대로 조회합니다.`);
                const ids = added.map(r => r.id);
                setVolumeUpdatingIds(new Set(ids));
                (async () => {
                    for (const id of ids) {
                        try {
                            const volRes = await AuthService.authAxios.post(
                                '/autocomplete/refresh-volumes',
                                { ids: [id] }
                            );
                            if (volRes.data?.rows?.[0]) {
                                setRows(prev =>
                                    prev.map(r => (r.id === id ? volRes.data.rows[0] : r))
                                );
                            }
                        } catch (_e) {
                            /* 무시 */
                        } finally {
                            setVolumeUpdatingIds(prev => {
                                const next = new Set(prev);
                                next.delete(id);
                                return next;
                            });
                        }
                    }
                })();
            } else {
                message.info(res.data.message || '추가할 키워드가 없습니다.');
            }
            setBulkModal({ open: false, text: '', loading: false });
        } catch (err) {
            message.error(apiErrToMessage(err.response?.data, err.response?.status));
            setBulkModal(prev => ({ ...prev, loading: false }));
        }
    };

    const handleMemoOpen = record => {
        setMemoModal({ open: true, id: record.id, value: record.memo || '' });
    };

    const handleMemoSave = async () => {
        setMemoSaving(true);
        try {
            await AuthService.authAxios.patch(`/autocomplete/memo/${memoModal.id}`, {
                memo: memoModal.value,
            });
            setRows(prev =>
                prev.map(r => (r.id === memoModal.id ? { ...r, memo: memoModal.value } : r))
            );
            setMemoModal({ open: false, id: null, value: '' });
            message.success('메모가 저장되었습니다.');
        } catch (err) {
            message.error(apiErrToMessage(err.response?.data, err.response?.status));
        } finally {
            setMemoSaving(false);
        }
    };

    const handleSingleDelete = async id => {
        try {
            await AuthService.authAxios.delete('/autocomplete/bulk', {
                data: { ids: [id] },
            });
            setRows(prev => prev.filter(r => r.id !== id));
            setSelectedRowKeys(prev => prev.filter(k => k !== id));
            message.success('삭제 완료');
        } catch (err) {
            message.error(apiErrToMessage(err.response?.data, err.response?.status));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('삭제할 항목을 선택해주세요.');
            return;
        }
        try {
            await AuthService.authAxios.delete('/autocomplete/bulk', {
                data: { ids: selectedRowKeys },
            });
            setRows(prev => prev.filter(r => !selectedRowKeys.includes(r.id)));
            setSelectedRowKeys([]);
            message.success(`${selectedRowKeys.length}개 삭제 완료`);
        } catch (err) {
            message.error(apiErrToMessage(err.response?.data, err.response?.status));
        }
    };

    // 순서 변경
    const moveRow = async (index, direction) => {
        const newRows = [...rows];
        let targetIndex;
        if (direction === 'up') targetIndex = index - 1;
        else if (direction === 'down') targetIndex = index + 1;
        else if (direction === 'top') targetIndex = 0;
        else targetIndex = newRows.length - 1;

        if (targetIndex < 0 || targetIndex >= newRows.length) return;

        if (direction === 'top' || direction === 'bottom') {
            const [item] = newRows.splice(index, 1);
            direction === 'top' ? newRows.unshift(item) : newRows.push(item);
        } else {
            [newRows[index], newRows[targetIndex]] = [newRows[targetIndex], newRows[index]];
        }

        setRows(newRows);
        try {
            await AuthService.authAxios.patch('/autocomplete/reorder', {
                orders: newRows.map((r, i) => ({ id: r.id, sort_order: i })),
            });
        } catch {
            message.error('순서 저장 실패');
            setRows(rows);
        }
    };

    // 히스토리 조회
    const fetchHistory = async (nextOffset = 0, append = false) => {
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: { gubun: 'autoComplete', offset: nextOffset },
            });
            if (res.data.success) {
                const newData = res.data.data;
                setHasMore(res.data.hasMore);
                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('히스토리 조회 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    const fetchResultHistory = async (id, offset = 0, append = false) => {
        if (offset === 0) {
            setResultHistory(prev => ({ ...prev, loading: true }));
        } else {
            setResultHistory(prev => ({ ...prev, loadingMore: true }));
        }
        try {
            const res = await AuthService.authAxios.get(`/autocomplete/result-history/${id}`, {
                params: { offset },
            });
            const { rows: newRows, hasMore } = res.data;
            setResultHistory(prev => ({
                ...prev,
                rows: append ? [...prev.rows, ...newRows] : newRows,
                hasMore,
                offset: offset + newRows.length,
                loading: false,
                loadingMore: false,
            }));
        } catch (err) {
            message.error('이력 조회 실패');
            setResultHistory(prev => ({ ...prev, loading: false, loadingMore: false }));
        }
    };

    const handleOpenResultHistory = record => {
        setResultHistory({
            open: true,
            id: record.id,
            keyword: record.keyword,
            rows: [],
            loading: true,
            loadingMore: false,
            hasMore: false,
            offset: 0,
        });
        fetchResultHistory(record.id, 0, false);
    };

    const handleOpenHistory = () => {
        setShowHistoryModal(true);
        setHistoryLoaded(false);
        setHistoryData([]);
        fetchHistory(0, false);
    };

    const handleDeleteHistory = async id => {
        try {
            await AuthService.authAxios.post('/log/delete', { id });
            setHistoryData(prev => prev.filter(h => h.id !== id));
        } catch (err) {
            message.error('히스토리 삭제 실패');
        }
    };

    const isSearchActive = !!searchText.trim();

    const columns = [
        {
            title: '검색어',
            dataIndex: 'keyword',
            key: 'keyword',
            width: isMobile ? 110 : 130,
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            render: (text, record) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span
                        className="ac-keyword-cell"
                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                        {text}
                        {record.memo && <span className="ac-mobile-memo-indicator">메모</span>}
                    </span>
                    <span className="ac-mobile-date">
                        {dayjs(record.last_searched_at).format('MM-DD HH:mm')}
                    </span>
                </div>
            ),
        },
        {
            title: () => {
                const expandableIds = rows.filter(r => r.autocomplete_results).map(r => r.id);
                const allExpanded =
                    expandableIds.length > 0 &&
                    expandableIds.every(id => expandedResultIds.has(id));
                const toggleAll = () => {
                    if (allExpanded) {
                        setExpandedResultIds(new Set());
                    } else {
                        setExpandedResultIds(new Set(expandableIds));
                    }
                };
                return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        추출검색어
                        <Button
                            type="link"
                            size="small"
                            icon={
                                allExpanded ? (
                                    <MenuOutlined style={{ fontSize: 10 }} />
                                ) : (
                                    <UnorderedListOutlined style={{ fontSize: 10 }} />
                                )
                            }
                            onClick={toggleAll}
                            style={{ padding: '0 2px', height: 'auto' }}
                            title={allExpanded ? '모두 접기' : '모두 펼치기'}
                        />
                    </span>
                );
            },
            dataIndex: 'autocomplete_results',
            key: 'autocomplete_results',
            render: (text, record) => {
                const keywords = (text || '')
                    .split(',')
                    .map(k => k.trim())
                    .filter(Boolean);
                let volumes = {};
                try {
                    volumes = record.search_volumes ? JSON.parse(record.search_volumes) : {};
                } catch (_e) {
                    /* invalid JSON */
                }
                const hasVolumes = Object.keys(volumes).length > 0;
                if (keywords.length === 0) return <Text type="secondary">결과 없음</Text>;

                const isVertical = expandedResultIds.has(record.id);

                const toggleVertical = () =>
                    setExpandedResultIds(prev => {
                        const next = new Set(prev);
                        isVertical ? next.delete(record.id) : next.add(record.id);
                        return next;
                    });

                const renderKw = (kw, i, arr) => (
                    <span
                        key={i}
                        style={{ display: 'inline-block', whiteSpace: 'nowrap', marginRight: 4 }}
                    >
                        {kw}
                        {hasVolumes && volumes[kw] != null ? (
                            <VolumeTag vol={volumes[kw]} showDetail={isVertical} />
                        ) : (
                            ''
                        )}
                        {!isVertical && i < arr.length - 1 ? ',' : ''}
                    </span>
                );

                return (
                    <div className="ac-results-cell">
                        {isVertical ? (
                            <span
                                style={{
                                    display: 'inline-grid',
                                    gridTemplateColumns: 'minmax(80px, 130px) 1fr',
                                    columnGap: 8,
                                    rowGap: 3,
                                    alignItems: 'center',
                                }}
                            >
                                {keywords.map((kw, i) => (
                                    <React.Fragment key={i}>
                                        <span style={{ wordBreak: 'break-all' }}>{kw}</span>
                                        <span>
                                            {hasVolumes && volumes[kw] != null ? (
                                                <VolumeTag vol={volumes[kw]} showDetail />
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </React.Fragment>
                                ))}
                                <span style={{ gridColumn: '1 / -1' }}>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<MenuOutlined style={{ fontSize: 10 }} />}
                                        onClick={toggleVertical}
                                        style={{ padding: '0 2px', height: 'auto' }}
                                    />
                                </span>
                            </span>
                        ) : (
                            <span>
                                {keywords.map((kw, i) => renderKw(kw, i, keywords))}
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<UnorderedListOutlined style={{ fontSize: 10 }} />}
                                    onClick={toggleVertical}
                                    style={{ padding: '0 2px', height: 'auto', marginLeft: 2 }}
                                />
                            </span>
                        )}
                        {volumeUpdatingIds.has(record.id) && (
                            <div className="ac-volume-updating">
                                <Spin size="small" /> 검색량 업데이트중
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: <span style={{ paddingLeft: 12 }}>메모</span>,
            dataIndex: 'memo',
            key: 'memo',
            width: 140,
            responsive: ['md'],
            render: (text, record) => (
                <span style={{ paddingLeft: 12, paddingRight: 4, display: 'block' }}>
                    {text ? <MemoCell text={text} id={record.id} /> : null}
                </span>
            ),
        },
        {
            title: <span style={{ paddingLeft: 12 }}>등록일</span>,
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            responsive: ['md'],
            render: val => (
                <span
                    style={{
                        paddingLeft: 12,
                        paddingRight: 12,
                        whiteSpace: 'nowrap',
                        display: 'block',
                    }}
                >
                    {dayjs(val).format('YYYY-MM-DD HH:mm')}
                </span>
            ),
        },
        {
            title: <span style={{ paddingLeft: 12 }}>최종조회일</span>,
            dataIndex: 'last_searched_at',
            key: 'last_searched_at',
            width: 160,
            responsive: ['md'],
            render: val => (
                <span
                    style={{
                        paddingLeft: 12,
                        paddingRight: 12,
                        whiteSpace: 'nowrap',
                        display: 'block',
                    }}
                >
                    {dayjs(val).format('YYYY-MM-DD HH:mm')}
                </span>
            ),
        },
        {
            title: <span style={{ paddingLeft: 12 }}>관리</span>,
            key: 'action',
            width: isMobile ? 190 : 280,
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            render: (_, record) => {
                const index = rows.findIndex(r => r.id === record.id);
                const isFirst = index === 0 || isSearchActive;
                const isLast = index === rows.length - 1 || isSearchActive;
                const { disabled: cdDisabled, remaining: cdRemaining } =
                    getResearchCooldownState(record);
                return (
                    <Space size={4} className="ac-action-space" style={{ paddingLeft: 12 }}>
                        <Button
                            size="small"
                            className="ac-btn-memo"
                            icon={<EditOutlined />}
                            onClick={() => handleMemoOpen(record)}
                        >
                            <span className="ac-btn-text">메모</span>
                        </Button>
                        <Tooltip
                            title={
                                cdDisabled
                                    ? `10분에 한 번만 가능합니다 (${cdRemaining}분 후 가능)`
                                    : ''
                            }
                        >
                            <Button
                                size="small"
                                className="ac-btn-research"
                                icon={<ReloadOutlined />}
                                loading={reSearchingIds.has(record.id)}
                                disabled={cdDisabled}
                                onClick={() => handleReSearch(record.id)}
                            >
                                <span className="ac-btn-text">재검색</span>
                            </Button>
                        </Tooltip>
                        <Button
                            size="small"
                            icon={<ClockCircleOutlined />}
                            onClick={() => handleOpenResultHistory(record)}
                            title="추출검색어 변경 이력"
                        />
                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items: [
                                    {
                                        key: 'top',
                                        label: '맨 위로',
                                        icon: <VerticalAlignTopOutlined />,
                                        disabled: isFirst,
                                    },
                                    {
                                        key: 'up',
                                        label: '위로',
                                        icon: <UpOutlined />,
                                        disabled: isFirst,
                                    },
                                    {
                                        key: 'down',
                                        label: '아래로',
                                        icon: <DownOutlined />,
                                        disabled: isLast,
                                    },
                                    {
                                        key: 'bottom',
                                        label: '맨 아래로',
                                        icon: <VerticalAlignBottomOutlined />,
                                        disabled: isLast,
                                    },
                                ],
                                onClick: ({ key }) => moveRow(index, key),
                            }}
                        >
                            <Button
                                size="small"
                                icon={<HolderOutlined />}
                                className="ac-order-btn"
                                disabled={isSearchActive}
                            />
                        </Dropdown>
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() =>
                                Modal.confirm({
                                    title: '항목 삭제',
                                    content: `"${record.keyword}" 항목을 삭제하시겠습니까?`,
                                    okText: '삭제',
                                    okType: 'danger',
                                    cancelText: '취소',
                                    onOk: () => handleSingleDelete(record.id),
                                })
                            }
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <div className="ac-page">
                <div className="page-content-inner">
                    <div className="ac-toolbar-card">
                        <div className="ac-toolbar-row">
                            <div className="ac-toolbar-left">
                                <span className="ac-toolbar-label">키워드 등록</span>
                                <Input
                                    placeholder="키워드 입력"
                                    value={keyword}
                                    onChange={e => setKeyword(e.target.value)}
                                    onPressEnter={handleRegister}
                                    className="ac-keyword-input"
                                    size="middle"
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    loading={registerLoading}
                                    onClick={handleRegister}
                                    className="ac-register-btn"
                                >
                                    등록
                                </Button>
                                <Button
                                    icon={<FileAddOutlined />}
                                    onClick={() =>
                                        setBulkModal({ open: true, text: '', loading: false })
                                    }
                                    className="ac-bulk-add-btn"
                                    disabled={rows.length >= maxKeywords}
                                >
                                    대량 등록
                                </Button>
                                <Divider type="vertical" className="ac-toolbar-divider" />
                                <Button
                                    icon={<HistoryOutlined />}
                                    size="middle"
                                    onClick={handleOpenHistory}
                                    className="ac-history-btn"
                                >
                                    조회 기록
                                </Button>
                                <Text
                                    type="secondary"
                                    className="ac-count-text"
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    등록: <strong style={{ color: '#333' }}>{rows.length}</strong> /{' '}
                                    {maxKeywords}
                                </Text>
                            </div>

                            <div className="ac-toolbar-right">
                                <Input
                                    placeholder="검색어 / 추출검색어 / 메모"
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    onPressEnter={handleSearch}
                                    allowClear
                                    className="ac-search-input"
                                    size="middle"
                                />
                                <Button
                                    icon={<SearchOutlined />}
                                    size="middle"
                                    onClick={handleSearch}
                                />
                                <Button
                                    size="middle"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReset}
                                    className="ac-reset-btn"
                                >
                                    초기화
                                </Button>
                                <Button
                                    className="excel-button"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExcelDownload}
                                    title="엑셀 다운로드"
                                />
                                {selectedRowKeys.length > 0 && (
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() =>
                                            Modal.confirm({
                                                title: '항목 삭제',
                                                content: `선택한 ${selectedRowKeys.length}개 항목을 삭제하시겠습니까?`,
                                                okText: '삭제',
                                                okType: 'danger',
                                                cancelText: '취소',
                                                onOk: handleBulkDelete,
                                            })
                                        }
                                        className="ac-bulk-delete-btn"
                                    >
                                        선택 삭제 ({selectedRowKeys.length})
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="ac-table-container">
                        <Table
                            rowSelection={{
                                selectedRowKeys,
                                onChange: setSelectedRowKeys,
                            }}
                            columns={columns}
                            dataSource={rows}
                            rowKey="id"
                            loading={loading}
                            pagination={false}
                            size="small"
                            scroll={{ x: isMobile ? 'max-content' : undefined }}
                            tableLayout={isMobile ? 'auto' : 'fixed'}
                            className="ac-table"
                            rowClassName={record =>
                                reSearchingIds.has(record.id) ? 'ac-row-loading' : ''
                            }
                            locale={{ emptyText: '등록된 키워드가 없습니다.' }}
                        />
                    </div>
                </div>
            </div>

            {/* 대량 등록 모달 */}
            <Modal
                title="대량 키워드 등록"
                open={bulkModal.open}
                onCancel={() => setBulkModal({ open: false, text: '', loading: false })}
                onOk={handleBulkRegister}
                okText={`등록 (${bulkPreview.toAdd.length}개)`}
                cancelText="취소"
                confirmLoading={bulkModal.loading}
                okButtonProps={{ disabled: bulkPreview.toAdd.length === 0 }}
                width={520}
            >
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        한 줄에 하나씩 입력하거나 붙여넣기 하세요.
                    </Text>
                </div>
                <TextArea
                    rows={8}
                    value={bulkModal.text}
                    onChange={e => setBulkModal(prev => ({ ...prev, text: e.target.value }))}
                    placeholder={'키워드1\n키워드2\n키워드3'}
                    style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
                />
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <Tag color="blue">슬롯 {bulkPreview.availableSlots}개 남음</Tag>
                    {bulkModal.text.trim() && (
                        <>
                            <Tag>입력 {bulkPreview.totalInput}개</Tag>
                            {bulkPreview.duplicates.length > 0 && (
                                <Tag color="orange">
                                    중복/기등록 {bulkPreview.duplicates.length}개 제외
                                </Tag>
                            )}
                            {bulkPreview.toAdd.length > 0 && (
                                <Tag color="green">추가될 키워드 {bulkPreview.toAdd.length}개</Tag>
                            )}
                            {bulkPreview.toAdd.length === 0 && bulkPreview.totalInput > 0 && (
                                <Tag color="red">추가할 새 키워드 없음</Tag>
                            )}
                        </>
                    )}
                </div>
                {bulkPreview.toAdd.length > 0 && (
                    <div
                        style={{
                            marginTop: 8,
                            maxHeight: 120,
                            overflowY: 'auto',
                            background: '#f8f9fa',
                            borderRadius: 4,
                            padding: '6px 10px',
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            추가될 키워드:
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '2px 8px',
                                marginTop: 4,
                            }}
                        >
                            {bulkPreview.toAdd.map((kw, i) => (
                                <Text key={i} style={{ fontSize: 12 }}>
                                    • {kw}
                                </Text>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>

            {/* 메모 모달 */}
            <Modal
                title="메모"
                open={memoModal.open}
                onOk={handleMemoSave}
                onCancel={() => setMemoModal({ open: false, id: null, value: '' })}
                okText="저장"
                cancelText="취소"
                confirmLoading={memoSaving}
                width={420}
            >
                <TextArea
                    rows={5}
                    value={memoModal.value}
                    onChange={e => setMemoModal(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="메모를 입력하세요."
                    style={{ resize: 'none' }}
                />
            </Modal>

            {/* 추출검색어 변경 이력 모달 */}
            <Modal
                title={`추출검색어 이력 — ${resultHistory.keyword}`}
                open={resultHistory.open}
                onCancel={() =>
                    setResultHistory({
                        open: false,
                        id: null,
                        keyword: '',
                        rows: [],
                        loading: false,
                        loadingMore: false,
                        hasMore: false,
                        offset: 0,
                    })
                }
                centered
                footer={[
                    <Button
                        key="close"
                        onClick={() =>
                            setResultHistory({
                                open: false,
                                id: null,
                                keyword: '',
                                rows: [],
                                loading: false,
                                loadingMore: false,
                                hasMore: false,
                                offset: 0,
                            })
                        }
                    >
                        닫기
                    </Button>,
                ]}
                width={640}
                styles={{ body: { padding: 0 } }}
            >
                <div style={{ height: '480px', overflowY: 'auto', padding: '12px 24px' }}>
                    {resultHistory.loading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                        </div>
                    ) : resultHistory.rows.length === 0 ? (
                        <Empty description="이력이 없습니다." />
                    ) : (
                        <>
                            <List
                                dataSource={resultHistory.rows}
                                renderItem={(item, idx) => {
                                    let volumes = {};
                                    try {
                                        volumes = item.search_volumes
                                            ? JSON.parse(item.search_volumes)
                                            : {};
                                    } catch (_e) {
                                        /* invalid JSON */
                                    }
                                    const hasVolumes = Object.keys(volumes).length > 0;
                                    const keywords = (item.results || '')
                                        .split(',')
                                        .map(k => k.trim())
                                        .filter(Boolean);
                                    return (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={
                                                    <Space split={<Divider type="vertical" />}>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            {dayjs(item.searched_at).format(
                                                                'YYYY-MM-DD HH:mm'
                                                            )}
                                                        </Text>
                                                        {idx === 0 && (
                                                            <Text
                                                                type="success"
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                최신
                                                            </Text>
                                                        )}
                                                    </Space>
                                                }
                                                description={
                                                    <Text
                                                        style={{ fontSize: '13px', color: '#444' }}
                                                    >
                                                        {keywords.map((kw, i) => (
                                                            <span key={i}>
                                                                {kw}
                                                                {hasVolumes &&
                                                                volumes[kw] != null ? (
                                                                    <VolumeTag vol={volumes[kw]} />
                                                                ) : (
                                                                    ''
                                                                )}
                                                                {i < keywords.length - 1
                                                                    ? ', '
                                                                    : ''}
                                                            </span>
                                                        ))}
                                                    </Text>
                                                }
                                            />
                                        </List.Item>
                                    );
                                }}
                            />
                            {resultHistory.hasMore && (
                                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                    <Button
                                        onClick={() =>
                                            fetchResultHistory(
                                                resultHistory.id,
                                                resultHistory.offset,
                                                true
                                            )
                                        }
                                        loading={resultHistory.loadingMore}
                                    >
                                        더보기
                                    </Button>
                                </div>
                            )}
                            {!resultHistory.hasMore && resultHistory.rows.length > 0 && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '8px',
                                        color: '#bbb',
                                        fontSize: '12px',
                                    }}
                                >
                                    전체 {resultHistory.rows.length}건
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>

            {/* 조회 기록 모달 */}
            <Modal
                title="자동완성 조회 기록"
                open={showHistoryModal}
                onCancel={() => setShowHistoryModal(false)}
                centered
                footer={[
                    <Button key="close" onClick={() => setShowHistoryModal(false)}>
                        닫기
                    </Button>,
                ]}
                width={600}
                styles={{ body: { maxHeight: '560px', overflowY: 'auto' } }}
            >
                {!historyLoaded ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    </div>
                ) : historyData.length === 0 ? (
                    <Empty description="조회 기록이 없습니다." />
                ) : (
                    <List
                        dataSource={historyData}
                        renderItem={item => {
                            const parts = {};
                            item.input.split('&').forEach(pair => {
                                const [k, v] = pair.split('=');
                                parts[k] = v;
                            });
                            return (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="del"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDeleteHistory(item.id)}
                                        />,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space split={<Divider type="vertical" />}>
                                                <Space>
                                                    {parts.action === 'volume' && (
                                                        <Text
                                                            type="success"
                                                            style={{
                                                                fontSize: '12px',
                                                                border: '1px solid',
                                                                padding: '0 4px',
                                                                borderRadius: '4px',
                                                            }}
                                                        >
                                                            검색량 조회
                                                        </Text>
                                                    )}
                                                    <Text strong>
                                                        키워드: {parts.keyword || '-'}
                                                    </Text>
                                                </Space>
                                                <Text type="secondary">
                                                    추출 {parts.count || 0}개
                                                </Text>
                                            </Space>
                                        }
                                        description={
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {item.updated_at || item.searched_at}
                                            </Text>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    >
                        {hasMore && (
                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                <Button onClick={() => fetchHistory(offset, true)}>더보기</Button>
                            </div>
                        )}
                    </List>
                )}
            </Modal>

            <KakaoFloatButton />
        </>
    );
};

export default AutoComplete;
